import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

// MUI components
import { Box, Button, Divider, Pagination, Popover, Typography } from "@mui/material";

// Components
import Modal from "components/Modal";
import CustomIcon from "components/CustomIcon";
import { Header } from "components/Layout/LayoutContent";
import NavButton from "components/HeaderNavigation/NavButton";
import { PreviousPaceModal } from "./components/PreviousPaceModal";
import {
    AverageHub,
    BehaviouralFilter,
    BehaviouralFiltersButtons,
    FunctionsFilter,
    HierarchiesFilter,
    IndividualsFilter,
    SavedFilters,
    TeamsFilter,
    TeamsModal,
} from "./components";
import BehaviouralBreakdown from "components/Charts/BehaviouralBreakdown";

// Hooks
import useBehaviouralComparison from "./useBehaviouralComparison";
import MessageUpgrade from "components/Pro/Messages/MessageUpgrade";

// Store
import icons from "enums/icons";
import { RootState } from "app/store";

// Enums
import { urlPaths } from "enums/urlPaths";

// Utils
import { isEmpty } from "lodash";
import theme from "assets/styles/themes";

// Styles
import useStyles from "./BehaviouralComparison.styles";

export const mockedSavedFilters = [
    { id: 0, name: "Finance Set" },
    { id: 1, name: "CFO Set" },
    { id: 2, name: "ProductTeam" },
];

const BehaviouralComparison = () => {
    const intl = useIntl();
    const classes = useStyles();
    const navigate = useNavigate();

    const {
        people,
        teams,
        functions,
        hierarchies,
        multiFilterData,
        chartData,
        savedFilters,
        showAverage,
        openTeamsModal,
        openPreviousPaceModal,
        openBenchmarkPopover,
        fetchingChartData,
        fetchingReports,
        setPeople,
        setTeams,
        setFunctions,
        setHierarchies,
        setMultiFilterData,
        handleToggleAverage,
        handleOnClear,
        handleOnSave,
        handleSelectPerson,
        handleSelectTeam,
        handlePageChange,
        handleOpenBenchmarkPopover,
        handleOpenTeamsModal,
        handleCloseTeamsModal,
        handleOpenPreviousPaceModal,
        handleClosePreviousPaceModal,
        handleSetResults,
        handleFilterDisabledData,
        handleUpdateReports,
    } = useBehaviouralComparison();

    const isLoadingChartData = fetchingChartData === "fetching" || !people?.selected?.length;

    // Header Buttons
    const headerOptions = [
        {
            id: "user-management",
            label: "User Management",
            icon: <CustomIcon icon={icons.key} sx={{ color: "primary.dark", fontSize: "13px !important" }} />,
            action: () => navigate(urlPaths.UserManagement),
        },
        {
            id: "pace-portal",
            label: "PACE Portal",
            icon: <CustomIcon icon={icons.equalizer} sx={{ color: "primary.dark", fontSize: "15px !important" }} />,
            action: () => navigate(urlPaths.PacePortal),
        },
        {
            id: "add-edit-team",
            label: "Add/Edit Teams",
            icon: <CustomIcon icon={icons.pencil} sx={{ color: "primary.dark", fontSize: "15px !important" }} />,
            action: handleOpenTeamsModal,
        },
    ];

    // Filter Tabs
    const behaviouralFilterTabs = [
        {
            label: "Individuals",
            intro: intl.formatMessage({ id: "behaviouralComparison.individualsTab.description" }),
            component: (
                <IndividualsFilter
                    data={people}
                    setData={handleSelectPerson}
                    handleSetResults={handleSetResults}
                    multiFilterData={multiFilterData}
                    setMultiFilterData={setMultiFilterData}
                    pagination={
                        <Pagination
                            className={classes.paginationStyled}
                            count={people.meta.pages}
                            page={people.meta.page_no}
                            onChange={(ev, newPage) => handlePageChange(ev, newPage, "individuals")}
                        />
                    }
                />
            ),
        },
        {
            label: "Averages",
            intro: intl.formatMessage({ id: "behaviouralComparison.averagesTab.description" }),
            component: (
                <TeamsFilter
                    data={teams}
                    setData={handleSelectTeam}
                    handleSetResults={handleSetResults}
                    handleOpenTeamsModal={handleOpenTeamsModal}
                    pagination={
                        <Pagination
                            className={classes.paginationStyled}
                            count={teams.meta.pages}
                            page={teams.meta.page_no}
                            onChange={(ev, newPage) => handlePageChange(ev, newPage, "teams")}
                        />
                    }
                />
            ),
            // tabs: [
            //     {
            //         label: "Teams",
            //         component: <TeamsFilter data={teams} setData={handleSelectTeam} />,
            //     },
            //     {
            //         label: "Functions",
            //         component: <FunctionsFilter data={functions} setData={setFunctions} />,
            //     },
            //     {
            //         label: "Hierarchies",
            //         component: <HierarchiesFilter data={hierarchies} setData={setHierarchies} />,
            //     },
            // ],
        },
    ];

    return (
        <>
            <Header backgroundColor="primary">
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 2 }}>
                    <Typography variant="h5" fontWeight={600}>
                        Behavioural Comparison
                    </Typography>
                    <Box>
                        {headerOptions.map((option) => (
                            <Button
                                key={option.id}
                                variant="text"
                                startIcon={option.icon}
                                onClick={option.action}
                                sx={{
                                    px: 0,
                                    mx: 2,
                                    height: "auto",
                                    minWidth: "auto",
                                    borderRadius: 0,
                                    color: "primary.dark",
                                    "&:hover": {
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                {option.label}
                            </Button>
                        ))}
                    </Box>
                    {/* <NavButton
                            variant="outlined"
                            isBackButton
                            aria-label="back to dashboard"
                            onClick={() => navigate(urlPaths.PaceDashboard)}
                        >
                            Back To Dashboard
                        </NavButton> */}
                </Box>
            </Header>

            <Box sx={{ p: 2 }}>
                <Typography className={classes.pageDescription}>
                    {intl.formatMessage({ id: "behaviouralComparison.header.description" })}
                </Typography>

                <Box className={classes.contentWrapper}>
                    {/* Behavioural Chart */}
                    <Box className={classes.chartWrapper}>
                        {!isEmpty(chartData) && (
                            <BehaviouralBreakdown
                                width={0}
                                height={0}
                                getData={(store: RootState) => handleFilterDisabledData(chartData)}
                                moreOptions={{
                                    shouldShowPaceDefinitions: true,
                                    hideCustomizeLabels: false,
                                    useCustomLegend: true,
                                }}
                            />
                        )}
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                variant="text"
                                color="primary"
                                startIcon={
                                    <CustomIcon
                                        icon={icons.paceIconPrevious}
                                        sx={{ opacity: isLoadingChartData ? 0.1 : 1 }}
                                    />
                                }
                                sx={{ padding: 0, height: "auto" }}
                                disabled={isLoadingChartData}
                                onClick={handleOpenPreviousPaceModal}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: isLoadingChartData
                                            ? theme.palette.button.deactivated
                                            : theme.palette.secondary.main,
                                        letterSpacing: "1.25px",
                                        textDecoration: "underline",
                                    }}
                                >
                                    View Previous PACE Data
                                </Typography>
                            </Button>
                        </Box>
                    </Box>
                    <Divider orientation="vertical" sx={{ borderColor: "neutrals.light", height: "auto" }} />
                    {/* Filters */}
                    <Box className={classes.filtersWrapper}>
                        {/* <SavedFilters data={savedFilters} /> */}
                        <AverageHub showAverage={showAverage} setShowAverage={handleToggleAverage} />
                        <BehaviouralFilter tabs={behaviouralFilterTabs} />
                        {/* <BehaviouralFiltersButtons onClear={handleOnClear} onSave={handleOnSave} /> */}

                        <Box display="flex" justifyContent="flex-end" marginTop={1}>
                            <Button
                                variant="text"
                                className={classes.discoverBenchmarkBtn}
                                onClick={handleOpenBenchmarkPopover}
                            >
                                {intl.formatMessage({ id: "behaviouralComparison.discoverBenchmarks.linkButton" })}
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* Benchmark Popover */}
                <Popover
                    open={openBenchmarkPopover}
                    onClose={handleOpenBenchmarkPopover}
                    anchorReference="anchorPosition"
                    anchorPosition={{
                        top: 104 + (window.innerHeight - 104) / 2,
                        left: 284 + (window.innerWidth - 284) / 2,
                    }}
                    slotProps={{
                        backdrop: {
                            className: classes.backdrop,
                        },
                    }}
                    PaperProps={{
                        style: {
                            transform: "translate(-50%, -50%)",
                        },
                    }}
                >
                    <MessageUpgrade
                        hasButtonIcon={true}
                        width={477}
                        variant="type3"
                        title={"behaviouralComparison.discoverBenchmarks.popover.title"}
                        descriptionTitle={"behaviouralComparison.discoverBenchmarks.popover.description"}
                        onClose={handleOpenBenchmarkPopover}
                    >
                        Get in touch with our team to find out how our Performance Benchmarking can help you identify
                        the behavioral culture that drives team success and fuels growth within your business.
                    </MessageUpgrade>
                </Popover>

                {/* Teams Modal */}
                <Modal
                    open={openTeamsModal}
                    // sx={{
                    //     display: "flex",
                    //     alignItems: "center",
                    //     justifyContent: "center",
                    // }}
                    // slotProps={{
                    //     backdrop: {
                    //         className: classes.modalBackdrop,
                    //     },
                    // }}
                >
                    <TeamsModal onClose={handleCloseTeamsModal} />
                </Modal>

                {/* Choose Previous Pace Modal */}
                <Modal open={openPreviousPaceModal}>
                    <PreviousPaceModal
                        people={people.data}
                        chartSeries={chartData.series_labels}
                        fetchingReports={fetchingReports}
                        handleUpdateReports={handleUpdateReports}
                        onClose={handleClosePreviousPaceModal}
                    />
                </Modal>
            </Box>
        </>
    );
};

export default BehaviouralComparison;
